import { gql } from 'graphql-tag';

const categoryListFragment = gql`
  fragment CategoryList on CategoryListResult {
    id
    title
    parentCategory {
      id
      title
    }
  }
`;

const categorySearchFragment = gql`
  fragment CategorySearch on CategorySearchResult {
    id
    title
    parentCategory {
      id
      title
    }
  }
`;

const articleAccessFragment = gql`
  fragment Access on AccessControl {
    access
    isInternal
    smartGroups {
      id
      name
    }
  }
`;

const pagingFragment = gql`
  fragment Paging on PageInfo {
    totalCount
    currentPage
    totalPages
  }
`;

export const searchArticlesQuery = gql`
  query SearchArticles(
    $pageUrl: URL!
    $languageId: LanguageID!
    $query: ArticleSearchQuery!
  ) {
    searchArticles(pageUrl: $pageUrl, languageId: $languageId, query: $query) {
      pageInfo {
        ...Paging
      }
      results {
        id
        title
        slug
        category {
          ...CategorySearch
          parentCategory {
            ...CategorySearch
          }
        }
        tags
        accessControl {
          isInternal
        }
      }
    }
  }
  ${categorySearchFragment}
  ${pagingFragment}
`;

export const articleQuery = gql`
  query Article($pageUrl: URL!, $languageId: LanguageID!, $id: ID!) {
    article(pageUrl: $pageUrl, languageId: $languageId, id: $id) {
      id
      title
      featuredImageUrl
      body
      bodyText
      lastPublishedAt
      contributors
      summary
      slug
      tags
      category {
        ...CategoryList
      }
      accessControl {
        ...Access
      }
    }
  }
  ${categoryListFragment}
  ${articleAccessFragment}
`;

export const suggestionsByTagQuery = gql`
  query SuggestionsByTag(
    $pageUrl: URL!
    $languageId: LanguageID!
    $tags: [String!]!
    $paging: PagingParams!
  ) {
    suggestedArticles: articlesByTag(
      pageUrl: $pageUrl
      languageId: $languageId
      query: { paging: $paging, tags: $tags }
    ) {
      pageInfo {
        ...Paging
      }
      results {
        id
        title
        slug
        tags
        category {
          ...CategorySearch
        }
        accessControl {
          restriction
          isInternal
        }
      }
    }
  }
  ${categorySearchFragment}
  ${pagingFragment}
`;

export const suggestionsQuery = gql`
  query Suggestions(
    $pageUrl: URL!
    $languageId: LanguageID!
    $text: String!
    $paging: PagingParams!
  ) {
    suggestedArticles: suggestArticles(
      pageUrl: $pageUrl
      languageId: $languageId
      query: { paging: $paging, text: $text }
    ) {
      ...ArticleListResults
    }
  }

  fragment ArticleListResults on ArticleSuggestionResultSet {
    pageInfo {
      ...Paging
    }
    results {
      id
      title
      slug
      tags
      category {
        ...CategorySearch
      }
      accessControl {
        restriction
        isInternal
      }
    }
  }
  ${categorySearchFragment}
  ${pagingFragment}
`;

export const resolutionSearchArticlesQuery = gql`
  query ResolutionSearchArticles(
    $pageUrl: URL!
    $languageId: LanguageID!
    $query: ArticleSearchQuery!
  ) {
    searchArticles(pageUrl: $pageUrl, languageId: $languageId, query: $query) {
      pageInfo {
        totalCount
        currentPage
        totalPages
      }
      results {
        id
        title
        slug
        accessControl {
          isInternal
          restriction
        }
        category {
          id
          title
        }
        tags
      }
    }
  }
`;

export const kcsSettingsQuery = gql`
  query KcsSettings {
    kcsSettings {
      mode
      showCloseButton
    }
  }
`;

export const userSettingsQuery = gql`
  query UserSettings($pageUrl: URL!) {
    userSettings(
      languageId: "en" # this api call needs a languageId, hard coded here because it doesn't matter, the result defaultLanguage.id is what's used at a later point
      pageUrl: $pageUrl
      initPageLoad: true
      loggedInOnly: false
      previewMode: false
    ) {
      company {
        defaultLanguage {
          id
        }
        languages {
          id
          nativeName
        }
      }
    }
  }
`;
